// external imports
import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Switch,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { EditOutlined, DeleteOutline, TranslateOutlined } from '@mui/icons-material';

// deterministic avatar color from a name, so a translator keeps the same swatch across rows
const AVATAR_COLORS = ['#6366f1', '#0ea5e9', '#10b981', '#ec4899', '#f59e0b', '#8b5cf6', '#ef4444'];
const colorForName = (name = '') => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const getInitials = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] || '')
    .join('')
    .toUpperCase() || '?';

// join translator names for a config (backend returns an array, today always length 1)
const translatorNames = (translators = []) =>
  translators
    .map((t) => t.employeeName)
    .filter(Boolean)
    .join(', ') || '—';

const CARD_SX = {
  background: '#ffffff',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  border: '1px solid #e2e8f0',
  overflow: 'hidden',
};

const HEADER_CELL_SX = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.06em',
  color: '#94a3b8',
  textTransform: 'uppercase',
};

const LanguageConfigTable = ({
  configs = [],
  loading,
  error,
  unauthorized,
  busyId,
  onToggle,
  onEdit,
  onDelete,
}) => {
  // ---- loading ----
  if (loading) {
    return (
      <Box sx={{ ...CARD_SX, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  // ---- error / not authorized ----
  if (error) {
    return (
      <Box
        sx={{
          ...CARD_SX,
          background: unauthorized ? '#fffbeb' : '#fef2f2',
          border: `1px solid ${unauthorized ? '#fde68a' : '#fecaca'}`,
          p: 4,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 700, color: unauthorized ? '#b45309' : '#b91c1c', mb: 0.5 }}>
          {unauthorized ? 'Not authorized' : 'Something went wrong'}
        </Typography>
        <Typography sx={{ color: unauthorized ? '#92400e' : '#dc2626', fontSize: 14 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  // ---- empty ----
  if (configs.length === 0) {
    return (
      <Box sx={{ ...CARD_SX, p: 6, textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <TranslateOutlined sx={{ color: '#6366f1', fontSize: 30 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
          No language configs yet
        </Typography>
        <Typography sx={{ color: '#64748b', fontSize: 14 }}>
          Use “Add Language Config” to map a target language to its translator.
        </Typography>
      </Box>
    );
  }

  // ---- table ----
  // grid columns: Language | Code | Translator | Active | Actions
  const gridTemplate = '1.4fr 0.9fr 1.8fr 0.9fr 1fr';

  return (
    <Box sx={CARD_SX}>
      {/* header row */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          alignItems: 'center',
          gap: 1,
          px: 3,
          py: 2,
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <Typography sx={HEADER_CELL_SX}>Language</Typography>
        <Typography sx={HEADER_CELL_SX}>Code</Typography>
        <Typography sx={HEADER_CELL_SX}>Assigned Translator</Typography>
        <Typography sx={HEADER_CELL_SX}>Active</Typography>
        <Typography sx={{ ...HEADER_CELL_SX, textAlign: 'right' }}>Actions</Typography>
      </Box>

      {/* data rows */}
      {configs.map((config, index) => {
        const isBusy = busyId === config._id;
        return (
          <Box
            key={config._id}
            sx={{
              display: 'grid',
              gridTemplateColumns: gridTemplate,
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 2,
              borderBottom: index === configs.length - 1 ? 'none' : '1px solid #f1f5f9',
              transition: 'background 0.15s',
              '&:hover': { background: '#fafbff' },
            }}
          >
            {/* Language */}
            <Typography sx={{ fontWeight: 600, color: '#1e293b', fontSize: 15 }}>
              {config.languageName}
            </Typography>

            {/* Code */}
            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: 13,
                color: '#64748b',
              }}
            >
              {config.languageCode}
            </Typography>

            {/* Translator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Avatar
                sx={{
                  bgcolor: colorForName(translatorNames(config.translators)),
                  width: 34,
                  height: 34,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {getInitials(config.translators?.[0]?.employeeName)}
              </Avatar>
              <Typography
                sx={{
                  color: '#334155',
                  fontSize: 14,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {translatorNames(config.translators)}
              </Typography>
            </Box>

            {/* Active toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isBusy ? (
                <CircularProgress size={20} sx={{ color: '#6366f1', ml: 1 }} />
              ) : (
                <Tooltip
                  title={
                    config.isActive
                      ? 'Active — click to deactivate'
                      : 'Inactive — click to activate'
                  }
                >
                  <Switch
                    checked={!!config.isActive}
                    onChange={() => onToggle(config)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#6366f1' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#6366f1',
                      },
                    }}
                  />
                </Tooltip>
              )}
            </Box>

            {/* Actions */}
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}
            >
              <Button
                onClick={() => onEdit(config)}
                startIcon={<EditOutlined sx={{ fontSize: 18 }} />}
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  color: '#4f46e5',
                  minWidth: 0,
                  '&:hover': { background: '#eef2ff' },
                }}
              >
                Edit
              </Button>
              <Tooltip title="Delete config">
                <span>
                  <IconButton
                    onClick={() => onDelete(config)}
                    disabled={isBusy}
                    size="small"
                    sx={{ color: '#ef4444', '&:hover': { background: '#fef2f2' } }}
                  >
                    <DeleteOutline sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default LanguageConfigTable;
